import { Binding, Result } from 'core/infrastructure';
import { DataGridColumn } from './DataGrid.Column';
import { Format, formatAsElementId } from './Utils';
import { Res } from 'core/api';
import { deepEquals } from 'core/utils';
import React from 'react';
import { FormComponent } from './Form';
import { NumericDataGrid, NumericDataGridCtor } from './NumericDataGrid';
import AutoCompleteTextBox from './AutoCompleteTextBox';
import { showCustom } from './Dialog';
import classNames from 'classnames';
import Button from './Button';
import { RemoteMultiSelectDialog } from './RemoteMultiSelectDialog';

function removeByItemKey<T>(allItems: T[], itemsToRemove: T[], itemKey: (item: T) => any): T[] {
	const containsByKey = (items: T[], item: T) => items.some(x => deepEquals(itemKey(x), itemKey(item)));
	return allItems.filter(item => !containsByKey(itemsToRemove, item));
}


interface Props<T> extends React.HTMLAttributes<Props<T>> {
	linkTo: Binding.PropertyPath<T>;
	gridHeight?: number;
	source: (filter: string) => Promise<any[]>;
	itemKey: (item: any) => any;
	columns: DataGridColumn<any>[];
	itemFormat?: Format;
	dialogTitle: string;
	dialogHelpText: string;
	boundValue?: any[];
	formId?: string;
	onSelectedItemsChanged?: (selectedItems: any[]) => void;
}

interface State {
	errorMsg?: string;
	selectedItems: any[];
}

export class RemoteMultiSelectNumericList<T> extends React.Component<Props<T>, State> implements FormComponent {

	private dataGrid = React.createRef<NumericDataGrid<T>>();

	constructor(props: Props<T>) {
		super(props);

		if (!(props.boundValue instanceof Array))
			throw new Error('CustomerSpecialPricesList no tiene un boundValue de tipo Array');

		this.state = this.createInitialState(props);
	}

	createInitialState(props: Props<T>): State {
		const selectedItems = props.boundValue || [];
		return {
			errorMsg: '',
			selectedItems: [...selectedItems],
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue() {
		if (!this.dataGrid.current!.isValid()) {
			this.setState({ errorMsg: Res.Msg.MSG_THERE_ARE_SOME_INVALID_NUMERIC_VALUES() });
			return Result.error();
		}

		this.setState({ errorMsg: '' });
		return Result.ok(this.dataGrid.current!.getData());
	}

	getPendingSource() {

		const search = this.props.source;
		return (filter: string) => search(filter).then(options => removeByItemKey(options, this.dataGrid.current!.getData(), this.props.itemKey));
	}

	getSelectedItems() {
		// Este método hace falta par los tests
		return this.state.selectedItems;
	}

	// Esta función dejo que sea accesible desde el exterior por si queremos eliminar
	// desde un botón de una columna
	removeItems(items: any[]) {
		const newSelection = removeByItemKey(this.dataGrid.current!.getData(), items, this.props.itemKey);
		this.setState({ selectedItems: newSelection });
	}

	buildAutoComplete() {

		const onOptionSelected = (option: any) => {
			const selectedItems = [...this.getSelectedItems()];
			selectedItems.push(option);
			this.setState({ selectedItems });
		};

		const availableSource = this.getPendingSource();

		const onShowDialog = () => this.showSelectItemsDialog().then(result => {
			const selectedItems = [...this.getSelectedItems()];
			if (result.ok)
				this.setState({ selectedItems: selectedItems.concat(result.value) });
		});

		return <AutoCompleteTextBox
			placeholder={Res.WA.WA_MULTI_SELECT_LIST_ADD_PLACE_HOLDER()}
			availableOptions={availableSource}
			formatOption={this.props.itemFormat}
			onShowSearchDialog={onShowDialog}
			onOptionSelected={onOptionSelected}
			errorMsg={this.state.errorMsg || ''} />;
	}

	protected buildSelectItemsDialog() {

		const gridProps = {
			source: this.getPendingSource(),
			columns: this.props.columns,
			itemKey: this.props.itemKey
		};

		return <RemoteMultiSelectDialog
			title={this.props.dialogTitle}
			helpText={this.props.dialogHelpText}
			grid={gridProps}
			acceptOnEnterKey={false} />;
	}

	protected showSelectItemsDialog() {
		return showCustom<any[]>(this.buildSelectItemsDialog());
	}

	render() {

		const Grid: NumericDataGridCtor<any> = NumericDataGrid;

		const onRemoveItems = () => this.removeItems(this.dataGrid.current!.getActiveRowData() ? [this.dataGrid.current!.getActiveRowData()] : []);

		const columns = this.props.columns;

		return <div className={classNames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>

			<div className='col-sm-12'>
				{this.buildAutoComplete()}

				<div className='m-t'>
					<div className='multi-select-list'>
						<Grid
							height={this.props.gridHeight || 200}
							itemKey={this.props.itemKey}
							ref={this.dataGrid}
							id={'customerSpecialPriceDataGrid_' + formatAsElementId(this.getPath(), this.props.formId || '')}
							columns={columns}
							initialData={this.getSelectedItems()}
							maxHeight={this.props.gridHeight || 175} />
					</div>
					<div className='multi-select-list-buttons'>
						<Button id='test_search_button' onClick={onRemoveItems}><i className='text-info fa fa-trash-o'></i></Button>
					</div>

				</div>
			</div>
		</div>;
	}
}

export type RemoteMultiSelectNumericListCtor<T> = new (props: Props<T>) => RemoteMultiSelectNumericList<T>;
